'use client';

import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { LiveWatchItem } from '@module/apis/client-side/live/type';
import SettingConfig from '@module/config/setting';
import { aspCond } from '@module/helpers/conditions/condition';
import { cn } from '@module/lib/utils';
import useIsDarkModeStore from '@module/stores/useIsDarkModeStore';
import useGoPlay from '@pc/hooks/play/useGoPlay';
import { useRouter } from 'next/navigation';

interface Props {
  title?: string;
  item: Omit<LiveWatchItem, 'dateTime'>;
  isOnEditing: boolean;
  isChecked: boolean;
  onCheckedChange: () => void;
  isKingFan?: boolean;
}

function PickUserItem({ title, item, isOnEditing, isChecked, onCheckedChange, isKingFan }: Props) {
  const { goPlay } = useGoPlay();
  const { isDark } = useIsDarkModeStore();
  const thumbUrl = item.thumbUrl ?? item.media?.ivsThumbnail;
  const router = useRouter();

  return (
    <li className='group w-1/4 p-[0_20px_40px_0] lg:w-1/5 xl:w-1/6'>
      <div className='break-all'>
        <div className='relative block overflow-hidden rounded-[8px] bg-center bg-cover bg-video-thumbnail bg-no-repeat pt-[56.25%]'>
          {isOnEditing && (
            <div className='absolute top-0 right-0 bottom-0 left-0 z-10 flex items-center justify-center bg-black bg-opacity-30'>
              <input
                type='checkbox'
                checked={isChecked}
                onChange={onCheckedChange}
                id='customCheckbox'
                className={cn(
                  'relative h-10 w-10 cursor-pointer appearance-none rounded-full border-2 border-[#dcdcdc] checked:border-primaryFg checked:bg-primaryFg ',
                  {
                    'border-[#555] text-[#555]': isDark,
                  },
                )}
              />
              <button type='button' className='-translate-x-1/2 absolute left-[50%]' onClick={onCheckedChange}>
                <FontAwesomeIcon
                  className={cn('text-[#dcdcdc]', {
                    'text-[#555]': isDark,
                    'bg-primaryFg text-white': isChecked,
                  })}
                  icon={faCheck}
                  size='lg'
                />
              </button>
            </div>
          )}
          <div
            className='hover:cursor-pointer'
            onClick={() => {
              if (item.media) {
                goPlay(item.media);
              } else if (!aspCond.if('iframe-asp')) {
                router.push(`/channel/${item.userId}/notice`);
              }
            }}
          >
            <img
              width='100%'
              height='100%'
              className='absolute inset-0 top-0 left-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-110'
              src={thumbUrl}
              onError={(e) => {
                if (e.currentTarget.src === item.userImg) return;
                e.currentTarget.src = item.userImg;
              }}
              alt={title}
            />
            {item?.media?.isLive && (
              <div className='absolute top-2 left-2'>
                {/* RT 대응 - 전환 후 삭제 예정 */}
                {item.media.latencyMode === 'RT' ? (
                  <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/live_plus.svg`} alt='live plus' />
                ) : (
                  <img src={`${SettingConfig.S3_BUCKET_URL}/img/icon/live_badge.png`} alt='live' />
                )}
              </div>
            )}
          </div>
        </div>
        <div className='video_card_wrapper mt-3 flex w-full'>
          <div className='video_card_area min-w-0 flex-1'>
            {title && (
              <div className='video_card_title block overflow-hidden overflow-ellipsis'>
                <div className='flex items-center overflow-hidden text-ellipsis whitespace-nowrap text-[16px] text-colorFg01'>
                  {/* 황유선 매니저님과 얘기해 제거할지 말지 추후 판단하기로하여 일단은 주석처리  (kh) */}
                  {/* {isPw && (
                    <img
                      src=`${SettingConfig.S3_BUCKET_URL}/img/icon/ico_lock.png'
                      width={12}
                      alt='lock'
                      className='mr-1 inline'
                    />
                  )} */}
                  {title}
                  {isKingFan && (
                    <img
                      src={`${SettingConfig.S3_BUCKET_URL}/img/grade/fan_king.png`}
                      alt='king'
                      className='ml-1 inline'
                      width={16}
                    />
                  )}
                </div>
              </div>
            )}
            <div className='video_card_name mt-1'>
              <button
                onClick={() => {
                  if (!aspCond.if('iframe-asp')) {
                    router.push(`/channel/${item.userId}/notice`);
                  }
                }}
                className={cn('video_card_channel inline-block max-w-full overflow-hidden align-top', {
                  'pointer-events-none': aspCond.if('iframe-asp'),
                })}
              >
                <p className='name_ellipsis inline-flex max-w-full overflow-hidden align-top'>
                  <span
                    className={cn('truncate text-[#777] text-sm', {
                      'hover:underline': !aspCond.if('iframe-asp'),
                    })}
                  >
                    {item.userNick}
                  </span>
                </p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
}

export default PickUserItem;
